import { SessionItem } from "@/mds/components/sessions/session-item";
import { SessionViewMode } from "@/mds/components/sessions/session.constants";
import {
  MDSSessionConfig,
  SessionEvent,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { MDSSessionNotificationInfo } from "@/mds/components/sessions/session-notification/session-notification.types";
import { MemCommonRichTextInputInstance } from "@mem-labs/common-editor";

// Import from split type files
import {
  MDSSessionEventsState,
  MDSSessionEventsActionTypes,
} from "./slices/events/mds-session-context.events.types";
import {
  MDSSessionUIState,
  MDSSessionUIActionTypes,
  MDSSessionArtifact,
  MDSSessionError,
} from "./slices/ui/mds-session-context.ui.types";

// Root state with all slices
export interface MDSSessionState {
  currentSessionId: string | null;
  events: MDSSessionEventsState;
  ui: MDSSessionUIState;
}

export enum MDSSessionAction {
  SetCurrentSessionId = "SET_CURRENT_SESSION_ID",
}

export interface MDSSessionSetCurrentSessionIdAction {
  type: MDSSessionAction.SetCurrentSessionId;
  payload: string | null;
}

// Unified action types
export type MDSSessionActionTypes =
  | MDSSessionEventsActionTypes
  | MDSSessionUIActionTypes
  | MDSSessionSetCurrentSessionIdAction;

// Context value interface
export interface MDSSessionContextValue {
  internalSessionItems: SessionItem[];
  finalizedSessionItems: SessionItem[];
  sessionViewMode: SessionViewMode;
  sessionPrevMode: SessionViewMode;
  sessionConfig: MDSSessionConfig;
  internalSessionEvents: SessionEvent[];
  sessionStatus: SessionStatus;
  activeArtifact: MDSSessionArtifact | null;
  countdownDurationMs: number;
  isCountdownPaused: boolean;
  isSessionTitleVisible: boolean;
  isCountdownVisible: boolean;
  isInputFormVisible: boolean;
  isSessionViewExpanded: boolean;
  sessionError: MDSSessionError | null;

  // Actions
  viewArtifact: ({ payload }: { payload: MDSSessionArtifact }) => void;
  closeArtifact: () => void;
  upsertSessionEvents: (events: SessionEvent[]) => void;
  updateSessionStatus: (status: SessionStatus) => void;
  minimizeSession: () => void;
  expandSession: () => void;
  expandFromNotification: () => void;
  toggleSessionItemExpandedState: ({ itemId }: { itemId: string }) => void;
  startCountdown: (durationMs: number) => void;
  stopCountdown: () => void;
  onCountdownComplete: () => void;
  handleHoverExtension: () => void;
  handleUnhoverExtension: () => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  handleInputChange: () => void;
  handleRetry?: () => void;
  setSessionError: (error: MDSSessionError) => void;
  clearSessionError: () => void;
  setPreviewMode: (isPreview: boolean) => void;
  expandFromPreview: () => void;
  publishNotification: (args: { notificationInfo: MDSSessionNotificationInfo }) => void;
  setNotificationState: (args: {
    isInNotificationState: boolean;
    notificationInfo?: MDSSessionNotificationInfo;
  }) => void;
  focusComboboxInput: () => void;
  setComboboxInputRef: (ref: MemCommonRichTextInputInstance | null) => void;
  setCurrentSessionId: (sessionId: string | null) => void;
}
